import { useState } from 'react';

import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ClassifiedListViewer = ({ list }) => {
  const [sortField, setSortField] = useState('email');
  const [sortDirection, setSortDirection] = useState('asc');
  const [tierFilter, setTierFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');

  // Mock contact data
  const mockContacts = [
    { id: 1, email: 'john.doe@example.com', tier: 'platinum', engagementScore: 98, verificationStatus: 'Deliverable_High_Conf', lastActivity: '2026-02-24' },
    { id: 2, email: 'jane.smith@company.com', tier: 'platinum', engagementScore: 96, verificationStatus: 'Deliverable_High_Conf', lastActivity: '2026-02-23' },
    { id: 3, email: 'bob.wilson@business.com', tier: 'gold', engagementScore: 92, verificationStatus: 'Deliverable_High_Conf', lastActivity: '2026-02-22' },
    { id: 4, email: 'alice.brown@email.com', tier: 'gold', engagementScore: 88, verificationStatus: 'Deliverable_Low_Conf', lastActivity: '2026-02-20' },
    { id: 5, email: 'charlie.davis@mail.com', tier: 'silver', engagementScore: 75, verificationStatus: 'Deliverable_High_Conf', lastActivity: '2026-02-18' },
    { id: 6, email: 'diana.evans@test.com', tier: 'silver', engagementScore: 72, verificationStatus: 'Unknown_Greylisted', lastActivity: '2026-02-15' },
    { id: 7, email: 'frank.garcia@sample.com', tier: 'bronze', engagementScore: 58, verificationStatus: 'Deliverable_Low_Conf', lastActivity: '2026-02-10' },
    { id: 8, email: 'grace.harris@demo.com', tier: 'bronze', engagementScore: 55, verificationStatus: 'Undeliverable', lastActivity: '2026-02-05' },
    { id: 9, email: 'henry.jackson@new.com', tier: 'lead', engagementScore: 0, verificationStatus: null, lastActivity: null },
    { id: 10, email: 'iris.king@fresh.com', tier: 'lead', engagementScore: 0, verificationStatus: null, lastActivity: null }
  ];

  const getTierColor = (tier) => {
    switch (tier) {
      case 'platinum':
        return 'bg-purple-500 text-white';
      case 'gold':
        return 'bg-yellow-500 text-white';
      case 'silver':
        return 'bg-slate-400 text-white';
      case 'bronze':
        return 'bg-orange-600 text-white';
      case 'lead':
        return 'bg-slate-600 text-white';
      default:
        return 'bg-slate-500 text-white';
    }
  };

  const getVerificationColor = (status) => {
    switch (status) {
      case 'Deliverable_High_Conf':
        return 'bg-success/10 text-success border-success/20';
      case 'Deliverable_Low_Conf':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'Undeliverable':
        return 'bg-error/10 text-error border-error/20';
      case 'Unknown_Greylisted':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getVerificationLabel = (status) => {
    if (!status) return 'Not Verified';
    return status?.replace(/_/g, ' ');
  };

  const tierOptions = [
    { value: 'all', label: 'All Tiers' },
    { value: 'platinum', label: 'Platinum' },
    { value: 'gold', label: 'Gold' },
    { value: 'silver', label: 'Silver' },
    { value: 'bronze', label: 'Bronze' },
    { value: 'lead', label: 'Lead' }
  ];

  const verificationOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'Deliverable_High_Conf', label: 'Deliverable (High)' },
    { value: 'Deliverable_Low_Conf', label: 'Deliverable (Low)' },
    { value: 'Undeliverable', label: 'Undeliverable' },
    { value: 'Unknown_Greylisted', label: 'Unknown' },
    { value: 'not_verified', label: 'Not Verified' }
  ];

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-heading font-semibold text-foreground">
              {list?.name}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {list?.totalContacts?.toLocaleString()} total contacts
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" iconName="Download">
              Export
            </Button>
            <Button variant="outline" size="sm" iconName="RefreshCw">
              Verify List
            </Button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-muted rounded-lg p-3">
            <div className="text-xs text-muted-foreground mb-1">Engagement Score</div>
            <div className="text-xl font-heading font-bold text-foreground">
              {list?.engagementScore}%
            </div>
          </div>
          <div className="bg-muted rounded-lg p-3">
            <div className="text-xs text-muted-foreground mb-1">Verification Coverage</div>
            <div className="text-xl font-heading font-bold text-foreground">
              {list?.verificationCoverage}%
            </div>
          </div>
          <div className="bg-muted rounded-lg p-3">
            <div className="text-xs text-muted-foreground mb-1">Deliverability Rate</div>
            <div className="text-xl font-heading font-bold text-success">
              {list?.deliverabilityRate > 0 ? `${list?.deliverabilityRate}%` : 'N/A'}
            </div>
          </div>
          <div className="bg-muted rounded-lg p-3">
            <div className="text-xs text-muted-foreground mb-1">Last Updated</div>
            <div className="text-sm font-medium text-foreground">
              {new Date(list?.lastUpdated)?.toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>
      {/* Tier Breakdown */}
      <div className="p-6 border-b border-border">
        <h3 className="text-sm font-medium text-foreground mb-4">Tier Distribution</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(list?.tierBreakdown || {})?.map(([tier, count]) => (
            <div key={tier} className="bg-muted rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-3 h-3 rounded-full ${getTierColor(tier)?.split(' ')?.[0]}`} />
                <span className="text-xs font-medium text-foreground capitalize">{tier}</span>
              </div>
              <div className="text-lg font-heading font-bold text-foreground">
                {count?.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">
                {((count / list?.totalContacts) * 100)?.toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Filters */}
      <div className="p-6 border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Filter by Tier"
            options={tierOptions}
            value={tierFilter}
            onChange={setTierFilter}
          />
          <Select
            label="Filter by Verification Status"
            options={verificationOptions}
            value={verificationFilter}
            onChange={setVerificationFilter}
          />
        </div>
      </div>
      {/* Contact Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Tier
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Engagement Score
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Verification Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Last Activity
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {mockContacts?.map((contact) => (
              <tr key={contact?.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-foreground">{contact?.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    getTierColor(contact?.tier)
                  }`}>
                    {contact?.tier?.charAt(0)?.toUpperCase() + contact?.tier?.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-foreground">{contact?.engagementScore}%</div>
                    <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary"
                        style={{ width: `${contact?.engagementScore}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                    getVerificationColor(contact?.verificationStatus)
                  }`}>
                    {getVerificationLabel(contact?.verificationStatus)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                  {contact?.lastActivity ? new Date(contact?.lastActivity)?.toLocaleDateString() : 'Never'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      <div className="p-6 border-t border-border flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing 1 to 10 of {list?.totalContacts?.toLocaleString()} contacts
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="ChevronLeft" disabled>
            Previous
          </Button>
          <Button variant="outline" size="sm" iconName="ChevronRight">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClassifiedListViewer;